import { useRef } from "react";

// Compact segmented rating control. Levels are shown exactly as configured, in the order received;
// their values and scores are not touched here. Arrow keys move focus; Enter or Space selects, so
// browsing the scale with the keyboard never saves a rating by accident.
export default function RatingScale({ levels = [], value = null, onChange, disabled = false, label, tone = "self", orgLabel = text => text, busy = false }) {
    const refs = useRef([]);
    const selectedIndex = levels.findIndex(level => level.id === value);
    const focusIndex = selectedIndex >= 0 ? selectedIndex : 0;
    const choose = index => { if (!disabled && levels[index] && levels[index].id !== value) onChange?.(levels[index].id); };
    const onKeyDown = (event, index) => {
        const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
        if (event.key in keys) {
            event.preventDefault();
            const next = (index + keys[event.key] + levels.length) % levels.length;
            refs.current[next]?.focus();
        } else if (event.key === "Home" || event.key === "End") {
            event.preventDefault();
            const next = event.key === "Home" ? 0 : levels.length - 1;
            refs.current[next]?.focus();
        }
    };
    return <div className={`cvi-routine-scale is-${tone}${disabled ? " is-disabled" : ""}${busy ? " is-busy" : ""}`} role="radiogroup" aria-label={label} aria-disabled={disabled || undefined}>
        {levels.map((level, index) => {
            const selected = level.id === value;
            return <button key={level.id} type="button" role="radio" aria-checked={selected} ref={node => { refs.current[index] = node; }}
                tabIndex={index === focusIndex ? 0 : -1} disabled={disabled} className={selected ? "is-selected" : ""}
                onClick={() => choose(index)} onKeyDown={event => onKeyDown(event, index)}>
                {orgLabel(level.label)}
            </button>;
        })}
    </div>;
}

// Read-only rating: the level name with a small position marker (its place in the configured scale).
export function RatingValue({ levels = [], levelId, tone = "manager", orgLabel = text => text, emptyText = "—" }) {
    const index = levels.findIndex(level => level.id === levelId);
    if (index < 0) return <span className="cvi-routine-value is-empty">{emptyText}</span>;
    const filled = levels.length - index;
    return <span className={`cvi-routine-value is-${tone}`}>
        <span className="cvi-routine-meter" aria-hidden="true">{levels.map((level, i) => <i key={level.id} className={i < filled ? "is-on" : ""} />)}</span>
        {orgLabel(levels[index].label)}
    </span>;
}

export function MonthTabs({ months = [1, 2, 3], value, onChange, summaries = {}, c }) {
    return <div className="cvi-routine-months" role="tablist" aria-label={c.months}>
        {months.map(m => {
            const s = summaries[m];
            const state = s?.result || "not_started";
            return <button key={m} type="button" role="tab" aria-selected={value === m} className={value === m ? "is-active" : ""} onClick={() => onChange(m)}>
                <span className={`cvi-routine-dot is-${state}`} aria-hidden="true" />
                <span className="cvi-routine-month-label">{c.month(m)}</span>
                <span className="cvi-routine-month-state">{c.history.result[state] || c.status[state]}</span>
            </button>;
        })}
    </div>;
}
